import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { createChart, CrosshairMode, CandlestickSeries, LineSeries, HistogramSeries } from 'lightweight-charts';

const ChartWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 500px;
    margin-top: 16px;
`;

const ChartContainer = styled.div`
    width: 100%;
    height: 100%;
    background: #1e1e1e;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #333;
`;

const MarkerLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 10;
    overflow: hidden;
`;

const LegendContainer = styled.div`
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: rgba(30, 30, 30, 0.7);
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #444;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #e5e5e5;
    font-weight: 500;
`;

const ColorInput = styled.input`
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    cursor: pointer;
    background: transparent;
    &::-webkit-color-swatch-wrapper {
        padding: 0;
    }
    &::-webkit-color-swatch {
        border: 1px solid #666;
        border-radius: 4px;
    }
`;

export default function BacktestChart({ chartData, onColorChange }) {
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const markerLayerRef = useRef(null);
    const [legendItems, setLegendItems] = useState([]);

    useEffect(() => {
        if (!chartContainerRef.current || !chartData || !chartData.candles) return;

        // Ensure time format is string 'YYYY-MM-DD' and ascending order
        const formattedCandles = chartData.candles.map(c => {
            let t = String(c.time);
            if (t.length === 8 && !t.includes('-')) {
                t = `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`;
            }
            return {
                time: t,
                open: parseFloat(c.open),
                high: parseFloat(c.high),
                low: parseFloat(c.low),
                close: parseFloat(c.close),
                volume: parseFloat(c.volume)
            };
        }).sort((a, b) => a.time.localeCompare(b.time));

        // Pre-classify all indicators
        const indicatorConfigs = [];
        let hasSubPane = false;

        if (chartData.indicators) {
            const midIdx = Math.floor(formattedCandles.length / 2);
            const refClose = formattedCandles[midIdx]?.close || formattedCandles[0]?.close || 1;

            for (const [indName, indArray] of Object.entries(chartData.indicators)) {
                const firstValidIdx = indArray.findIndex(v => v !== null && v !== undefined);
                if (firstValidIdx < 0) continue;

                const firstVal = indArray[firstValidIdx];
                const ratio = refClose !== 0 ? Math.abs(firstVal / refClose) : 999;

                const isNamedOscillator = /rsi|macd|hist|osc|sto|cci|mfi/i.test(indName);
                const isTrendIndicator = /_trend$/i.test(indName);
                const isVolumeRelated = /volume|vol_ma|volume_ma/i.test(indName);
                const isRatioOutOfRange = ratio < 0.05 || ratio > 20;

                const isOverlay = !isNamedOscillator && !isVolumeRelated && !isRatioOutOfRange && !isTrendIndicator;

                let scaleId = 'right';
                if (!isOverlay && !isTrendIndicator) {
                    hasSubPane = true;
                    if (/rsi/i.test(indName)) scaleId = 'rsi';
                    else if (/macd|hist/i.test(indName)) scaleId = 'macd';
                    else if (/volume|vol/i.test(indName)) scaleId = 'volume';
                    else scaleId = 'sub';
                }

                // Retrieve readable name from indicatorDefs if available
                let readableName = indName;
                if (chartData.indicatorDefs) {
                    const parts = indName.split('_');
                    const baseId = parts.length >= 2 && parts[0] === 'ind' ? `${parts[0]}_${parts[1]}` : indName;
                    const suffix = parts.length > 2 ? ` ${parts.slice(2).join(' ')}` : '';
                    
                    const def = chartData.indicatorDefs.find(d => d.id === baseId);
                    if (def) {
                        let pStr = '';
                        if (def.period) pStr = def.period;
                        else if (def.shortPeriod && def.longPeriod) pStr = `${def.shortPeriod},${def.longPeriod}`;
                        readableName = `${def.type}${pStr ? '(' + pStr + ')' : ''}${suffix.toUpperCase()}`;
                    }
                }

                indicatorConfigs.push({
                    indName, readableName, indArray, isOverlay, scaleId,
                    isHist: /hist/i.test(indName),
                    isTrend: isTrendIndicator
                });
            }
        }

        const chart = createChart(chartContainerRef.current, {
            layout: {
                background: { type: 'solid', color: '#1e1e1e' },
                textColor: '#d1d4dc',
            },
            grid: {
                vertLines: { color: 'rgba(42, 46, 57, 0.5)' },
                horzLines: { color: 'rgba(42, 46, 57, 0.5)' },
            },
            crosshair: { mode: CrosshairMode.Normal },
            rightPriceScale: {
                borderColor: 'rgba(197, 203, 206, 0.8)',
                ...(hasSubPane && { scaleMargins: { top: 0.05, bottom: 0.3 } })
            },
            timeScale: {
                borderColor: 'rgba(197, 203, 206, 0.8)',
                timeVisible: true,
            },
        });

        chartRef.current = chart;

        const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#00e676', downColor: '#ff4ea1',
            borderDownColor: '#ff4ea1', borderUpColor: '#00e676',
            wickDownColor: '#ff4ea1', wickUpColor: '#00e676',
        });
        candleSeries.setData(formattedCandles);

        // Add indicator series
        const overlayColors = ['#f39c12', '#9b59b6', '#3498db', '#e74c3c', '#1abc9c'];
        let overlayColorIdx = 0;
        let subPaneColorIdx = 0;
        const usedSubScaleIds = new Set();
        const newLegendItems = [];

        // Retrieve TBM_Trend data for coloring SuperTrend (if present)
        const trendConfig = indicatorConfigs.find(c => c.isTrend);
        const trendArray = trendConfig ? trendConfig.indArray : null;

        indicatorConfigs.forEach(config => {
            if (config.isTrend) return;

            // Special handling for SuperTrend: split into up/down colored segments
            if (/supertrend/i.test(config.indName) && trendArray) {
                const upData = [];
                const downData = [];

                for (let i = 0; i < formattedCandles.length; i++) {
                    if (config.indArray[i] === null || config.indArray[i] === undefined) continue;
                    const time = formattedCandles[i].time;
                    const value = config.indArray[i];
                    const trend = trendArray[i];

                    if (trend === 1) {
                        upData.push({ time, value });
                        // Add overlap point from previous down segment
                        if (i > 0 && trendArray[i - 1] === -1 && config.indArray[i - 1] != null) {
                            // Insert transition point at the start of upData
                        }
                    } else if (trend === -1) {
                        downData.push({ time, value });
                    }

                    // At transition points, add the point to BOTH series so lines connect
                    if (i > 0 && trendArray[i - 1] != null && trend !== trendArray[i - 1]) {
                        if (trend === 1) {
                            // Just switched to up: also put this point in downData to complete the segment
                            downData.push({ time, value });
                        } else {
                            // Just switched to down: also put this point in upData to complete the segment
                            upData.push({ time, value });
                        }
                    }
                }

                // Sort by time (transition overlap points may be slightly out of order)
                upData.sort((a, b) => a.time.localeCompare(b.time));
                downData.sort((a, b) => a.time.localeCompare(b.time));

                if (upData.length > 0) {
                    const upSeries = chart.addSeries(LineSeries, {
                        color: '#00e676', lineWidth: 2, priceScaleId: 'right', title: 'ST ▲'
                    });
                    upSeries.setData(upData);
                }

                if (downData.length > 0) {
                    const downSeries = chart.addSeries(LineSeries, {
                        color: '#ff4ea1', lineWidth: 2, priceScaleId: 'right', title: 'ST ▼'
                    });
                    downSeries.setData(downData);
                }

                return; // Skip default rendering for this indicator
            }

            // Default rendering for non-SuperTrend indicators
            
            // Extract user-defined color from indicatorDefs if present
            let userColor = null;
            let baseId = config.indName;
            if (chartData.indicatorDefs) {
                const parts = config.indName.split('_');
                baseId = parts.length >= 2 && parts[0] === 'ind' ? `${parts[0]}_${parts[1]}` : config.indName;
                const def = chartData.indicatorDefs.find(d => d.id === baseId);
                if (def && def.color) {
                    userColor = def.color;
                }
            }

            let series;
            // To ensure the input[type=color] accepts it, fallback to valid hex instead of naming color or rgb/rgba for the input value.
            // But we can let lightweight charts render the string. We will parse hex if available.
            if (config.isHist) {
                const finalColor = userColor || '#26a69a';
                series = chart.addSeries(HistogramSeries, {
                    color: finalColor, priceScaleId: config.scaleId, title: config.readableName || config.indName
                });
                newLegendItems.push({ id: baseId, readableName: config.readableName || config.indName, color: finalColor, series });
            } else {
                // VWAP gets white, other overlays get the rotating palette
                let color;
                if (userColor) {
                    color = userColor;
                } else if (/vwap/i.test(config.indName)) {
                    color = '#ffffff';
                } else if (config.isOverlay) {
                    color = overlayColors[overlayColorIdx % overlayColors.length];
                } else {
                    color = overlayColors[subPaneColorIdx % overlayColors.length];
                }
                series = chart.addSeries(LineSeries, {
                    color, lineWidth: 2, priceScaleId: config.scaleId, title: config.readableName || config.indName
                });
                newLegendItems.push({ id: baseId, readableName: config.readableName || config.indName, color, series });
                if (config.isOverlay) overlayColorIdx++;
                else subPaneColorIdx++;
            }

            if (!config.isOverlay) usedSubScaleIds.add(config.scaleId);

            const seriesData = [];
            for (let i = 0; i < formattedCandles.length; i++) {
                if (config.indArray[i] !== null && config.indArray[i] !== undefined) {
                    seriesData.push({
                        time: formattedCandles[i].time,
                        value: config.indArray[i],
                        ...(config.isHist && { color: config.indArray[i] >= 0 ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)' })
                    });
                }
            }
            if (seriesData.length > 0) series.setData(seriesData);
        });

        usedSubScaleIds.forEach(sid => {
            chart.priceScale(sid).applyOptions({ scaleMargins: { top: 0.75, bottom: 0 } });
        });

        // ── BUY / SELL Markers (DOM overlay) ──
        const signalPoints = [];
        if (chartData.signals) {
            for (let i = 0; i < chartData.signals.length; i++) {
                const sig = chartData.signals[i];
                if (sig !== 'BUY' && sig !== 'SELL') continue;
                if (i >= formattedCandles.length) continue;
                signalPoints.push({
                    type: sig,
                    time: formattedCandles[i].time,
                    price: sig === 'BUY' ? formattedCandles[i].low : formattedCandles[i].high,
                });
            }
        }

        const renderMarkers = () => {
            if (!markerLayerRef.current) return;
            markerLayerRef.current.innerHTML = '';

            for (const sp of signalPoints) {
                const x = chart.timeScale().timeToCoordinate(sp.time);
                const y = candleSeries.priceToCoordinate(sp.price);

                if (x === null || y === null || x < 0 || y < 0) continue;

                const el = document.createElement('div');
                const isBuy = sp.type === 'BUY';
                el.style.cssText = `
                    position:absolute;
                    left:${x}px;
                    top:${isBuy ? y + 4 : y - 22}px;
                    transform:translateX(-50%);
                    color:${isBuy ? '#00e676' : '#ff4ea1'};
                    font-size:16px;
                    font-weight:bold;
                    text-align:center;
                    line-height:1;
                    text-shadow: 0 0 4px rgba(0,0,0,0.8);
                `;
                el.textContent = isBuy ? '▲' : '▼';
                markerLayerRef.current.appendChild(el);
            }
        };

        // Render markers initially and on every visible range change
        renderMarkers();
        chart.timeScale().subscribeVisibleLogicalRangeChange(renderMarkers);

        chart.timeScale().fitContent();

        const handleResize = () => {
            if (chartContainerRef.current) {
                chart.applyOptions({ width: chartContainerRef.current.clientWidth });
                renderMarkers();
            }
        };

        const resizeObserver = new ResizeObserver(() => {
            handleResize();
        });
        
        if (chartContainerRef.current) {
            resizeObserver.observe(chartContainerRef.current);
        }

        window.addEventListener('resize', handleResize);

        // Make sure to commit legend items to state
        setLegendItems(newLegendItems);

        return () => {
            if (chartContainerRef.current) {
                resizeObserver.unobserve(chartContainerRef.current);
            }
            resizeObserver.disconnect();
            
            window.removeEventListener('resize', handleResize);
            chart.timeScale().unsubscribeVisibleLogicalRangeChange(renderMarkers);
            chart.remove();
        };
    }, [chartData]);

    const handleColorChange = (item, newColor) => {
        item.series.applyOptions({ color: newColor });
        setLegendItems(prev => prev.map(p => p.id === item.id ? { ...p, color: newColor } : p));
        if (onColorChange) {
            onColorChange(item.id, newColor);
        }
    };

    return (
        <ChartWrapper>
            <ChartContainer ref={chartContainerRef} />
            <MarkerLayer ref={markerLayerRef} />
            {legendItems.length > 0 && (
                <LegendContainer>
                    {legendItems.map((item, i) => (
                        <LegendItem key={item.id + i}>
                            <ColorInput 
                                type="color" 
                                value={item.color} 
                                onChange={(e) => handleColorChange(item, e.target.value)} 
                            />
                            {item.readableName}
                        </LegendItem>
                    ))}
                </LegendContainer>
            )}
        </ChartWrapper>
    );
}
